import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import {
  LaneAttentionAlert,
  LaneAttentionService,
  MULTI_ELIGIBLE_SELECTION_REASON,
} from '../../../services/lane-attention.service';
import { MultiEligibleSelectionModalStateService } from '../../../components/multi-eligible-selection-modal/services/multi-eligible-selection-modal-state.service';

/**
 * Persistent stackable staff alerts for LPR cases that cannot auto-open the gate.
 * Auto-dismisses after 5 minutes; closable manually.
 */
@Component({
  selector: 'app-lane-attention-stack',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './lane-attention-stack.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './lane-attention-stack.component.scss',
})
export class LaneAttentionStackComponent implements OnInit, OnDestroy {
  private readonly laneAttentionService = inject(LaneAttentionService);
  private readonly multiEligibleModalState = inject(MultiEligibleSelectionModalStateService);
  private subscription?: Subscription;

  alerts: LaneAttentionAlert[] = [];

  ngOnInit(): void {
    this.subscription = this.laneAttentionService.alerts$.subscribe(alerts => {
      this.alerts = alerts;
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  dismiss(id: string, event?: Event): void {
    event?.stopPropagation();
    this.laneAttentionService.dismiss(id);
  }

  /** CWS-1679: a multi-eligible card from another lane opens that lane's chooser here. */
  canOpen(alert: LaneAttentionAlert): boolean {
    const options = alert.payload?.['purchase_options'];
    return (
      alert.reason === MULTI_ELIGIBLE_SELECTION_REASON &&
      Array.isArray(options) &&
      options.length > 0 &&
      Boolean(alert.lpr_entry_id)
    );
  }

  /** Enter on the card itself; Enter on the nested dismiss button must only dismiss. */
  onCardEnter(alert: LaneAttentionAlert, event: Event): void {
    if (event.target !== event.currentTarget) {
      return;
    }
    this.open(alert);
  }

  /** The Open button; stops the click so the card's own handler does not open it twice. */
  onOpenClick(alert: LaneAttentionAlert, event: Event): void {
    event.stopPropagation();
    this.open(alert);
  }

  /** "CA 9ULL460", without repeating the state when the plate read already starts with it. */
  plateLabel(alert: LaneAttentionAlert): string {
    const plate = String(alert.plate ?? '').trim();
    const state = String(alert.state ?? '').trim();
    if (!state || plate.toUpperCase().startsWith(state.toUpperCase())) {
      return plate;
    }
    return `${state} ${plate}`;
  }

  open(alert: LaneAttentionAlert): void {
    if (!this.canOpen(alert)) {
      return;
    }
    this.multiEligibleModalState.openModal({
      notification: {
        type: 'multi-eligible-selection',
        message: alert.message,
        timestamp: new Date(),
        data: {
          ...alert.payload,
          event: 'multi-eligible-selection',
          lpr_entry_id: alert.lpr_entry_id,
          is_lpr_upload: true,
        },
      },
      behavior: 'replace',
      shouldStore: false,
    });
  }

  trackById(_index: number, alert: LaneAttentionAlert): string {
    return alert.id;
  }
}
